import { useEffect, useState, useCallback } from 'react'
import { getQualAbbrevs } from '@/lib/qualificationUtils'
import { createClient } from '@/lib/supabase/client'
import { useUser } from '@/contexts/UserContext'

// Simple cross-component event so the inbox can instantly tell the badge to refresh
export function triggerNotificationBadgeRefresh() {
  window.dispatchEvent(new CustomEvent('notif-badge-refresh'))
}

/**
 * Counts unread notifications given the full targeted set and the
 * employee's read set.
 */
export function countUnread(
  notifs: { id: string }[],
  reads: { notification_id: string | null }[]
): number {
  const allIds = new Set(notifs.map(n => n.id))
  const readIds = new Set(reads.map(r => r.notification_id))
  return [...allIds].filter(id => !readIds.has(id)).length
}

export function useUnreadNotificationCount(): { count: number } {
  const { employee, unit } = useUser()
  const [count, setCount] = useState(0)

  const fetchCount = useCallback(async () => {
    if (!employee?.id) return

    const employeeId = employee.id
    const unitName = unit?.name
    const supabase = createClient()
    // Build qual abbreviation set for push targeting
    const qualAbbrevs = getQualAbbrevs(employee as any)

    const orParts = [
      'target_employee_ids.is.null',
      `target_employee_ids.cs.{${employeeId}}`,
    ]
    qualAbbrevs.forEach(a => orParts.push(`target_roles.cs.{${a}}`))
    if (unitName) orParts.push(`target_units.cs.{${unitName}}`)

    const [{ data: notifs }, { data: reads }] = await Promise.all([
      supabase
        .from('push_notifications')
        .select('id')
        .or(orParts.join(',')),
      supabase
        .from('notification_reads')
        .select('notification_id')
        .eq('employee_id', employeeId),
    ])

    setCount(countUnread(notifs || [], reads || []))
  }, [employee?.id, unit?.name])

  useEffect(() => {
    if (!employee?.id) return

    fetchCount()
    const interval = setInterval(fetchCount, 60_000)

    // Instantly refresh when the inbox marks notifications as read or deletes them
    window.addEventListener('notif-badge-refresh', fetchCount)

    return () => {
      clearInterval(interval)
      window.removeEventListener('notif-badge-refresh', fetchCount)
    }
  }, [fetchCount, employee?.id])

  return { count }
}
